import React, { useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import { accent7b as accent, useSlide7b, Seg } from "./slide7b.data";
import { FONT_BODY, FONT_DISPLAY, FONT_MONO } from "theme/fonts";

/* Portrait/mobile layout for Slide 7b — flowing, scrollable, no canvas.
   Reads the same content as the desktop slide from slide7b.data.ts. */

const font = FONT_BODY;
const mono = FONT_MONO;

const lbl: CSSProperties = { fontFamily: mono, fontSize: 10, letterSpacing: 2.5, color: accent, textTransform: "uppercase", margin: "0 0 8px" };
const txt: CSSProperties = { fontFamily: FONT_BODY, fontStyle: "normal", fontSize: 15, lineHeight: 1.7, color: "#cde0f2", margin: 0 };

const renderSegs = (segs: Seg[], boldColor: string) =>
  segs.map((s, i) =>
    s.b ? <b key={i} style={{ color: boldColor, fontWeight: 700 }}>{s.t}</b>
    : s.em ? <em key={i}>{s.t}</em>
    : <React.Fragment key={i}>{s.t}</React.Fragment>
  );

const HTLDSlide7bMobile: React.FC = () => {
  const { meta: slide7bMeta, still: slide7bStill, awareness: slide7bAwareness, watch: slide7bWatch } = useSlide7b();
  const fontRef = useRef<HTMLLinkElement | null>(null);
  useEffect(() => {
    if (!document.querySelector('link[href*="Space+Grotesk"]')) {
      const l = document.createElement("link"); l.rel = "stylesheet";
      l.href = "https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;1,300;1,400;1,700&display=swap";
      document.head.appendChild(l); fontRef.current = l;
    }
  }, []);

  return (
    <div style={{ background: "#040f18", color: "#d0e8f8", fontFamily: font, padding: "22px 18px 40px", boxSizing: "border-box", height: "100%", width: "100%", overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
      {/* breadcrumb */}
      <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: 2, color: "#8a6ab0", textTransform: "uppercase", marginBottom: 8 }}>
        {slide7bMeta.breadcrumbPre} · <span style={{ color: accent }}>{slide7bMeta.breadcrumbAccent}</span>
      </div>

      {/* title */}
      <h1 style={{ fontFamily: FONT_DISPLAY, fontStyle: "normal", fontWeight: 600, fontSize: 26, lineHeight: 1.2, margin: "0 0 4px", color: "#e8f4ff" }}>
        {slide7bMeta.titlePre}<span style={{ color: accent }}>{slide7bMeta.titleAccentWord}</span>{slide7bMeta.titlePost}
      </h1>
      <p style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 300, fontSize: 14, color: "#9bbdd6", margin: "0 0 20px", lineHeight: 1.5 }}>
        {slide7bMeta.subtitle}
      </p>

      {/* body grows still */}
      <div style={{ marginBottom: 18 }}>
        <div style={lbl}>{slide7bMeta.stillLabel}</div>
        <p style={txt}>{renderSegs(slide7bStill, "#d8c0ff")}</p>
      </div>

      {/* awareness need not follow */}
      <div style={{ marginBottom: 18 }}>
        <div style={lbl}>{slide7bMeta.awarenessLabel}</div>
        <p style={txt}>{renderSegs(slide7bAwareness, "#d8c0ff")}</p>
      </div>

      {/* watch callout */}
      <div style={{ background: "rgba(192,144,224,0.06)", border: "1px solid rgba(192,144,224,0.22)", borderRadius: 10, padding: "14px 16px" }}>
        <div style={lbl}>{slide7bMeta.watchLabel}</div>
        <p style={{ ...txt, color: "#d0b8f0" }}>{renderSegs(slide7bWatch, "#ffffff")}</p>
      </div>
    </div>
  );
};

export default HTLDSlide7bMobile;
